import Number from 'helpers/Number';
import { action } from 'mobx';
import { ContentPageService } from 'modules/content';
import { UiService } from 'modules/ui';

import { CAMPAIGNS_STATUS, CAMPAIGN_TYPE } from './constants';

import SalesStore from '../SalesStore';

import Request from '@hotmart/request';

class CampaignsService {
  fetchCampaigns = async () => {
    if (!SalesStore.campaigns.loading && !SalesStore.campaigns.error) {
      return;
    }

    this.setData({ loading: true, error: false });

    let list = {};

    try {
      const response = await Request('sales.campaign');

      const ids = await Promise.all(
        response.map(async (item) => {
          const { campaignId, pageId } = item;
          const visualizeLink = await this.fetchCampaignPreviewURL(pageId);

          list = {
            ...list,
            [campaignId]: { ...item, visualizeLink }
          };

          return campaignId;
        })
      );

      this.setData({ list, ids, loading: false, error: false });

      ids.forEach(this.fetchNumberOfSales);
    } catch (error) {
      UiService.handleError(error);

      this.setData({ loading: false, error: true });
    }
  };

  fetchCampaignPreviewURL = async (pageHash) => {
    if (!pageHash) {
      return '';
    }

    try {
      const response = await ContentPageService.fetchPreviewURL({ pageHash });

      return response && response.redirectUrl;
    } catch {
      return '';
    }
  };

  fetchNumberOfSales = async (campaignId) => {
    const { type } = SalesStore.campaigns.list[campaignId] || {};

    if (type === CAMPAIGN_TYPE.AFFILIATE) {
      this.setNumberOfSales(campaignId, null);

      return;
    }

    this.setNumberOfSales(campaignId, { loading: true });

    try {
      const { salesAmount } =
        (await Request('sales.campaign.sales.amount', {
          campaignId
        })) || {};

      const numberOfSales = {
        campaignId,
        value: Number.format(salesAmount || 0),
        loading: false,
        error: false
      };

      this.setNumberOfSales(campaignId, numberOfSales);
    } catch (error) {
      this.setNumberOfSales(campaignId, { loading: false, error: true });
    }
  };

  activeCampaign = async (campaignId) => {
    try {
      await Request('sales.campaign.activate', { campaignId });

      this.setCampaignStatus(campaignId, CAMPAIGNS_STATUS.ENABLED);
    } catch (error) {
      UiService.handleError(error);
    }
  };

  inactiveCampaign = async (campaignId) => {
    try {
      await Request('sales.campaign.inactivate', { campaignId });

      this.setCampaignStatus(campaignId, CAMPAIGNS_STATUS.DISABLED);
    } catch (error) {
      UiService.handleError(error);
    }
  };

  @action
  setCampaignStatus(campaignId, status) {
    this.setData({
      list: {
        ...SalesStore.campaigns.list,
        [campaignId]: {
          ...SalesStore.campaigns.list[campaignId],
          status
        }
      }
    });
  }

  @action
  setNumberOfSales(campaignId, data) {
    this.setData({
      numberOfSales: {
        ...SalesStore.campaigns.numberOfSales,
        [campaignId]: data
      }
    });
  }

  @action
  setData(data) {
    Object.assign(SalesStore, {
      campaigns: { ...SalesStore.campaigns, ...data }
    });
  }
}

export default new CampaignsService();
